import { OAuthTypeEnum } from "../../../const/oauth.mjs";
import { OAuth2ClientTypeEnum } from "../../../const/oauth2Client.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/auth/oauth2Client.ts
const AppleFormPostProvider = z.enum(["apple", "apple2"]);
const startQuerySchema = z.object({
	clientType: OAuth2ClientTypeEnum,
	redirectUrl: z.string().min(1),
	referralCode: z.string().optional(),
	deviceId: z.string().min(1).optional()
}).superRefine((data, ctx) => {
	if (data.clientType !== "web" && !data.deviceId) ctx.addIssue({
		code: "custom",
		message: "deviceId is required for mobile clients",
		path: ["deviceId"]
	});
});
const callbackQuerySchema = z.object({
	code: z.string().optional(),
	state: z.string().optional(),
	error: z.string().optional()
});
const appleCallbackBodySchema = z.object({
	code: z.string().optional(),
	state: z.string().optional(),
	error: z.string().optional(),
	user: z.string().optional()
});
const nativeBodySchema = z.object({
	idToken: z.string().min(1),
	nonce: z.string().optional(),
	authorizationCode: z.string().optional(),
	referralCode: z.string().optional()
});
const tokenOutputSchema = z.object({
	token: z.string(),
	isNew: z.boolean()
});
const exchangeBodySchema = z.object({ exchangeCode: z.string().min(1) });
const exchangeHeadersSchema = z.object({ "x-device-id": z.string().min(1) });
const redirectEnvelopeSchema = z.object({ headers: z.object({ location: z.string() }) });
const oauth2ClientContract = oc.prefix("/auth").tag("OAuth2 Client").router({
	start: oc.route({
		method: "GET",
		path: "/oauth/{provider}/start",
		summary: "Initiate OAuth flow with the named provider (302 to provider). Top-level navigation so the pre-auth cookie is set in a first-party context. `clientType` distinguishes web vs mobile in-app browser.",
		successStatus: 302,
		outputStructure: "detailed",
		inputStructure: "detailed"
	}).input(z.object({
		params: z.object({ provider: OAuthTypeEnum }),
		query: startQuerySchema
	})).output(redirectEnvelopeSchema),
	callback: oc.route({
		method: "GET",
		path: "/oauth/{provider}/callback",
		summary: "Provider callback. 302 to record.redirectUrl on success, 302 to ${appBaseUrl}/auth/error?code=… on failure.",
		successStatus: 302,
		outputStructure: "detailed",
		inputStructure: "detailed"
	}).input(z.object({
		params: z.object({ provider: OAuthTypeEnum }),
		query: callbackQuerySchema
	})).output(redirectEnvelopeSchema),
	appleCallback: oc.route({
		method: "POST",
		path: "/oauth/{provider}/callback",
		summary: "Apple form-post callback variant (apple, apple2)",
		successStatus: 302,
		outputStructure: "detailed",
		inputStructure: "detailed"
	}).input(z.object({
		params: z.object({ provider: AppleFormPostProvider }),
		body: appleCallbackBodySchema
	})).output(redirectEnvelopeSchema),
	native: oc.route({
		method: "POST",
		path: "/oauth/{provider}/native",
		summary: "Exchange a native-SDK id_token for a session JWT",
		inputStructure: "detailed"
	}).input(z.object({
		params: z.object({ provider: OAuthTypeEnum }),
		body: nativeBodySchema
	})).output(tokenOutputSchema),
	exchange: oc.route({
		method: "POST",
		path: "/oauth/exchange",
		summary: "Redeem a single-use exchange code for a session JWT. Requires X-Device-Id matching the deviceId supplied at /start.",
		inputStructure: "detailed"
	}).input(z.object({
		body: exchangeBodySchema,
		headers: exchangeHeadersSchema
	})).output(tokenOutputSchema)
});
//#endregion
export { oauth2ClientContract };
